import { useState } from "react";

import { useMutation, useQuery } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, Modal } from "react-bootstrap";
import { useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod/v4";

import { showContactPath } from "@path/contact_paths";

import { may } from "@/abilities";
import { addDangerFlash } from "@/components/flash/flashReducer";
import TTModal from "@/components/TTModal";
import { AddButton, CancelButton, SaveButton } from "@/containers/buttons";
import Loading from "@/containers/Loading";
import { NoDataTag } from "@/containers/NoData";
import RHCheck from "@/containers/RHCheck";
import RHInput from "@/containers/RHInput";
import TTFormGroup from "@/containers/TTFormGroup";
import { MutateContactDocument } from "@/gql/graphql";
import { ContactsDocument } from "@/gql/graphql";
import handleError, { MutationError } from "@/handleError";
import { useAppDispatch, useAppSelector } from "@/hooks";
import useDebounce from "@/hooks/useDebounce";
import { getPeerNo } from "@/new_utils";
import { setCallContact } from "@/store/acdReducer";
import { sessionSelector } from "@/store/sessionReducer";
import { indexDate } from "@/utils/dates";

import type { ContactValuesInterface } from "./types";
import { ADDITIONAL_BUTTONS } from "./utils";

type PropsType = {
  toggle: () => void;
  show: boolean;
  filterValue: string;
  setFilterValue: (value: string) => void;
  call: AcdCallEventType;
};

const validationSchema = z
  .object({
    isCompany: z.boolean(),
    salutation: z.string().trim().nullable(),
    companyName: z.string().trim().nullable(),
    firstname: z.string().trim().nullable(),
    lastname: z.string().trim().nullable(),
  })
  .check((ctx) => {
    if (ctx.value.isCompany && !ctx.value.companyName) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value.companyName,
        message: "contacts:form.company_name_missing",
        path: ["companyName"],
      });
    } else if (!ctx.value.isCompany) {
      if (!ctx.value.salutation) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value.salutation,
          message: "contacts:form.salutation_missing",
          path: ["salutation"],
        });
      }

      if (!ctx.value.firstname && !ctx.value.lastname) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value.firstname,
          message: "contacts:form.firstname_lastname_missing",
          path: ["firstname"],
        });

        ctx.issues.push({
          code: "custom",
          input: ctx.value.lastname,
          message: "contacts:form.firstname_lastname_missing",
          path: ["lastname"],
        });
      }
    }
  });

type ValuesType = z.infer<typeof validationSchema>;

const initialValues: ValuesType = {
  isCompany: false,
  salutation: "",
  firstname: "",
  lastname: "",
  companyName: "",
};

const FALLBACK = () => <></>;

export default function AssignModal({ show, toggle, filterValue, setFilterValue, call }: PropsType) {
  const dispatch = useAppDispatch();
  const session = useAppSelector(sessionSelector);
  const { t } = useTranslation(["acd", "contacts", "translation"]);
  const navigate = useNavigate();
  const filter = useDebounce(filterValue);
  const [newButtonPressed, setNewButtonPressed] = useState(false);
  const [createContactMutation] = useMutation(MutateContactDocument);

  const peerNo = getPeerNo(call.event);

  const AdditionalButtons =
    ADDITIONAL_BUTTONS[session.currentCustomer.identifier]?.[session.currentProject.identifier] || FALLBACK;

  const { data, loading } = useQuery(ContactsDocument, {
    variables: {
      customerId: session.currentCustomer.id,
      projectId: session.currentProject.id,
      filters: {
        limit: "15",
        offset: "0",
        order: "UPDATED_AT",
        direction: "DESC",
        search: filter || filterValue,
        ctype: "MAIN",
        ignoreProject: true,
      },
    },
    skip: !show || !may(session, "contacts"),
  });

  async function saveContact(values: ValuesType) {
    const contactValues: ContactValuesInterface = {
      isCompany: !!values.companyName,
      companyName: values.companyName,
      infos: [],
    };

    if (contactValues.isCompany && (values.firstname || values.lastname)) {
      contactValues.subContacts = [
        {
          firstname: values.firstname,
          lastname: values.lastname,
          salutation: values.salutation,
        },
      ];
    } else if (!contactValues.isCompany) {
      contactValues.salutation = values.salutation;
      contactValues.firstname = values.firstname;
      contactValues.lastname = values.lastname;
    }

    if (peerNo) {
      contactValues.infos.push({
        itype: values.companyName ? "WORK_PHONE" : "PRIVATE_PHONE",
        value: peerNo.replace(/^00(?!4)/, "0"),
      });
    }

    try {
      const { data } = await createContactMutation({
        variables: {
          contact: contactValues,
          customerId: session.currentCustomer.id,
          projectId: session.currentProject.id,
        },
      });

      if (!data?.mutateContact) {
        throw new MutationError();
      }

      contactChosen(data.mutateContact);
    } catch (e) {
      handleError(e);
      dispatch(addDangerFlash(t("translation:global.general_error")));
    }
  }

  function contactChosen(cnt: ContactRefType) {
    const cntSession = { ...session, currentProject: cnt.project ?? session.currentProject };

    dispatch(setCallContact(cnt));
    navigate(showContactPath(cntSession, cnt));

    toggle();
  }

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ValuesType>({
    resolver: zodResolver(validationSchema),
    defaultValues: initialValues,
    mode: "onBlur",
  });

  const isCompany = useWatch({ control, name: "isCompany" });

  return (
    <TTModal show={show} onHide={toggle} size="lg">
      <Form onSubmit={handleSubmit(saveContact)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("acd:incoming_call.incoming_call")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Trans parent="p" t={t} i18nKey="acd:incoming_call.incoming_call_desc">
            Eingehender Call für die Line „{{ line: call.line?.name || "" }}“ ({{ no: peerNo }}). Begrüßung:{" "}
            <em>{{ greeting: call.line?.greeting || "" }}</em>
          </Trans>

          <TTFormGroup>
            <Form.Label htmlFor="search">{t("acd:incoming_call.search_for_contact")}</Form.Label>
            <Form.Control id="search" value={filterValue} onChange={(ev) => setFilterValue(ev.target.value)} />
          </TTFormGroup>

          {loading && <Loading />}

          {!loading && !!data?.contacts && (
            <ul className="TT-incoming-call-contacts-list">
              <NoDataTag data={data.contacts} />

              {data.contacts.map((cnt) => (
                <li key={cnt.id} className="TT-incoming-call-contact-card" onClick={() => contactChosen(cnt)}>
                  <div className="contact-name">{cnt.displayName}</div>
                  <div className="contact-status">{cnt.status ? cnt.status.name : t("contacts:no_state_chosen")}</div>
                  <div className="project-name">{cnt.project?.name}</div>
                  <div className="last-contact">
                    {t("contacts:list.last_contact")}{" "}
                    {cnt.lastContact ? indexDate(cnt.lastContact.insertedAt) : t("translation:global.never")}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {newButtonPressed && (
            <>
              <fieldset>
                <TTFormGroup>
                  <RHCheck control={control} name="isCompany" id="is-company" label={t("contacts:company")} />
                </TTFormGroup>

                {isCompany && (
                  <RHInput
                    control={control}
                    name="companyName"
                    id="companyName"
                    namespace="contacts"
                    label={t("contacts:human_field_names.company_name")}
                  />
                )}
                {!isCompany && (
                  <>
                    <RHInput
                      control={control}
                      name="salutation"
                      id="salutation"
                      namespace="contacts"
                      label={t("contacts:human_field_names.salutation")}
                    />

                    <RHInput
                      control={control}
                      name="firstname"
                      id="firstname"
                      namespace="contacts"
                      label={t("contacts:human_field_names.firstname")}
                    />

                    <RHInput
                      control={control}
                      name="lastname"
                      id="lastname"
                      namespace="contacts"
                      label={t("contacts:human_field_names.lastname")}
                    />
                  </>
                )}
              </fieldset>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {!newButtonPressed && (
            <AddButton type="button" onClick={() => setNewButtonPressed(true)}>
              {t("acd:incoming_call.create_contact")}
            </AddButton>
          )}

          {newButtonPressed && (
            <SaveButton disabled={isSubmitting} type="submit">
              {t("translation:global.save")}
            </SaveButton>
          )}

          <CancelButton type="button" onClick={toggle} disabled={isSubmitting}>
            {t("translation:global.cancel")}
          </CancelButton>

          <AdditionalButtons contactChosen={contactChosen} />
        </Modal.Footer>
      </Form>
    </TTModal>
  );
}
